import React, { useEffect, useState } from 'react';
import { useRecoilValue, useRecoilState } from 'recoil';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import dayjs from 'dayjs';
import {
  DatePicker,
  Table,
  Tag,
  Avatar,
  Modal,
  Upload,
  Button,
  Popconfirm
} from 'antd';
import { faClipboardCheck, faEye, faPencil, faPlus, faTrash } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Link from 'antd/es/typography/Link';
import { userAtom, notificationsAtom, userPrivilagesAtom } from '../../../atoms/Atoms';
import MainPage from '../../shared-components/main-page/MainPage';
import { H1, H4 } from '../../shared-components/typography/Title';
import api from '../../../api/api';
import {
  toCurrency,
  thousand,
  getDatePresets
} from '../../../functions';
import Select from '../../shared-components/form/Select';
import CustomButton from '../../shared-components/buttons/Button';
import Form from '../../shared-components/form/Form';
import DateSelector from '../../shared-components/form/DatePicker';
import Input from '../../shared-components/form/Input';

function PartsInventory() {
  const { t, i18n } = useTranslation();
  const { RangePicker } = DatePicker;
  const user = useRecoilValue(userAtom);
  const userPrivilages = useRecoilValue(userPrivilagesAtom);
  const [notifications, setNotifications] = useRecoilState(notificationsAtom);
  const [dateRange, setDateRange] = useState({});
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [reportData, setReportData] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [lastSearch, setLastSearch] = useState('');

  function getData(selectedPage, sTerm) {
    if (!dateRange.start || !dateRange.end) {
      return;
    }
    setLoading(true);
    setLastSearch(sTerm);
    setCurrentPage(selectedPage);
    const startDate = dayjs(dateRange.start);
    const endDate = dayjs(dateRange.end);
    const utcOffset = startDate.utcOffset();
    const startString = startDate.add(-1 * utcOffset, 'minute').format('YYYY-MM-DDThh:mm:ss');
    const endString = endDate.add(-1 * utcOffset, 'minute').add(1, 'day').add(-1, 'second').format('YYYY-MM-DDThh:mm:ss');
    api.post('reports/inventory/parts', {
        // StartDate: startString,
        // EndDate: endString,
        Page: currentPage,
        Size: pageSize,
        ForExport: false,
        Query: sTerm
      })
      .then((response) => {
        setLoading(false);
        if (response.data.success) {
          setReportData(response.data.data);
        } else {
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: response.data.error,
              error: true,
            }
          ]);
        }
      })
      .catch((error) => {
        setLoading(false);
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          }
        ]);
      });
  }

  useEffect(() => {
    setDateRange({
      start: dayjs().startOf('week').format('MMM DD, YYYY'),
      end: dayjs().endOf('day').format('MMM DD, YYYY')
    });
  }, []);

  useEffect(() => {
    getData(0, lastSearch);
  }, [pageSize, dateRange]); // add filters sorts here

  return (
    <MainPage>
      <H1 text={t('nonserialized_inventory_on_hand')} />
      <div className="space-y-2">
        <div className="w-full mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-2">
          {/* <Select
            className="col-span-1 mt-2"
            disabled={loading}
            label={t('type')}
            id="typefilter"
            name="typefilter"
            value={incidentalTypeFilter}
            onChange={(obj) => setIncidentalTypeFilter(obj.target.value)}
            options={types}
          />
          <Select
            className="col-span-1 mt-2"
            disabled={loading}
            label={t('approval_status')}
            id="approvalfilter"
            name="approvalfilter"
            value={incidentalApprovalFilter}
            onChange={(obj) => setIncidentalApprovalFilter(obj.target.value)}
            options={statusFilters}
          />
          <Select
            className="col-span-1 mt-2"
            disabled={loading}
            label={t('payment_status')}
            id="paymentfilter"
            name="paymentfilter"
            value={incidentalPaymentFilter}
            onChange={(obj) => setIncidentalPaymentFilter(obj.target.value)}
            options={statusFilters}
          /> */}
          {/* <div className="col-span-1 md:col-span-2 lg:col-start-4 lg:col-span-1 xl:col-start-5">
            <RangePicker
              disabled={loading}
              className="w-full h-11 mt-2 bg-transparent"
              disabledDate={(current) => current && current.valueOf() > Date.now()}
              presets={getDatePresets(t)}
              value={[
                dayjs(dateRange?.start),
                dayjs(dateRange?.end)
              ]}
              onChange={(obj) => {
                setDateRange({
                  start: obj[0],
                  end: obj[1]
                });
              }}
              format="MMM DD, YYYY"
            />
          </div> */}
        </div>
        <div className="flex space-x-2">
          <Input
            label={t('search')}
            type="text"
            name="search"
            key="search"
            value={searchTerm}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                getData(0, searchTerm)
              }
            }}
            onChange={(obj) => setSearchTerm(obj.target.value)}
          />
          <CustomButton
            className="bg-primaryColor px-4"
            textColor="text-primaryTextColor"
            loaderColor="bg-primaryTextColor"
            borderColor="border-primaryBorderColor"
            loading={loading}
            text={t('search')}
            onClick={() => getData(0, searchTerm)}
          />
        </div>
        <Table
          bordered
          locale={{
            emptyText: t('no_data')
          }}
          title={() => (
            <div className="flex">
              <div className="space-x-2">
                <strong>{t('total')}:</strong>
                {reportData?.inventory && (
                  <span>{thousand(reportData?.total)}</span>
                )}
              </div>
              <div className="space-x-2 ml-auto">
                <strong>{t('last_updated_on')}:</strong>
                {reportData?.lastUpdatedAt && (
                  <span>{dayjs(reportData?.lastUpdatedAt).add(-4, 'hour').format('MMM DD, YYYY hh:mm A')}</span>
                )}
              </div>
              {/* <CustomButton
                className="bg-primaryColor px-2 ml-auto"
                textColor="text-primaryTextColor"
                loaderColor="bg-primaryTextColor"
                borderColor="border-primaryBorderColor"
                text={(
                  <div>
                    <FontAwesomeIcon className="mr-2" icon={faPlus} />
                    {t('create_new')}
                  </div>
                )}
                loading={loading}
                onClick={() => {
                  submissionData.setValues({
                    id: null,
                    resubmit: false,
                    type: 'FIXED',
                    date: dayjs().toString(),
                    description: '',
                    amount: 0,
                    approvalStatus: 'PENDING',
                    paymentStatus: 'PENDING',
                    month: dayjs().month() + 1,
                    year: dayjs().year(),
                    status: 0,
                    approvalDate: null,
                    paymentApprovalDate: null,
                    submittedById: null,
                    approvedById: null,
                    paymentApprovedById: null,
                    reviewerComments: '',
                    submitter: null,
                    approver: null,
                    paymentApprover: null,
                    isNew: true,
                    isReadOnly: false,
                    openModal: true
                  });
                  setAttachments([]);
                }}
              /> */}
            </div>
          )}
          rowKey="id"
          loading={loading}
          className="mt-2"
          pagination={{
            pageSize,
            showSizeChanger: true,
            current: currentPage + 1,
            total: reportData?.total,
            onChange: (page, size) => {
              if (page - 1 !== currentPage) {
                setCurrentPage(page - 1);
                getData(page - 1);
              } else {
                setPageSize(size);
              }
            }
          }}
          dataSource={reportData?.inventory}
          columns={[
            {
              title: t('site'),
              key: 'site',
              align: 'center',
              className: 'text-sm',
              render: (row, object, index) => (
                <span>{row.site}</span>
              ),
            },
            {
              title: t('order_status'),
              key: 'orderStatus',
              align: 'center',
              className: 'text-sm',
              render: (row, object, index) => (
                <span>{row.orderStatus}</span>
              ),
            },
            {
              title: t('lot_number'),
              key: 'lotNumber',
              align: 'center',
              className: 'text-sm',
              render: (row, object, index) => (
                <span>{row.lotNumber}</span>
              ),
            },
            {
              title: t('make'),
              key: 'make',
              align: 'center',
              className: 'text-sm',
              render: (row, object, index) => (
                <span>{row.make}</span>
              ),
            },
            {
              title: t('model'),
              key: 'model',
              align: 'center',
              className: 'text-sm',
              render: (row, object, index) => (
                <span>{row.model}</span>
              ),
            },
            {
              title: t('description'),
              key: 'shortDescription',
              align: 'center',
              className: 'text-sm',
              render: (row, object, index) => (
                <span>{row.shortDescription}</span>
              ),
            },
            {
              title: t('location'),
              key: 'location',
              align: 'center',
              className: 'text-sm',
              render: (row, object, index) => (
                <span>{row.location}</span>
              ),
            },
            {
              title: t('price_cny'),
              key: 'priceCNY',
              align: 'center',
              className: 'text-sm',
              render: (row, object, index) => (
                <span>{toCurrency(row.priceCNY)}</span>
              ),
            },
            {
              title: t('date_checked_in'),
              key: 'dateCheckedIn',
              align: 'center',
              className: 'text-sm',
              render: (row, object, index) => (
                <span>{row.dateCheckedIn ? dayjs(row.dateCheckedIn).format('MMM DD, YYYY hh:mm A') : '-'}</span>
              ),
            },
            {
              title: t('qty'),
              key: 'qty',
              align: 'center',
              className: 'text-sm',
              render: (row, object, index) => (
                <span>{thousand(row.qty)}</span>
              ),
            }
          ]}
          expandable={{
            expandRowByClick: false,
            rowExpandable: (record) => true,
            expandedRowRender: (record, index) => (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                <div className="col-span-1">
                  <div>
                    <strong>{t('pallet_id')}: </strong>
                    {record.palletId?.length > 0 && (
                      <span>{record.palletId}</span>
                    )}
                    {!record.palletId?.length > 0 && (
                      <span className="text-gray-400">N/A</span>
                    )}
                  </div>
                  <div>
                    <strong>{t('cb_part_number')}: </strong>
                    {record.cbPartNumber?.length > 0 && (
                      <span>{record.cbPartNumber}</span>
                    )}
                    {!record.cbPartNumber?.length > 0 && (
                      <span className="text-gray-400">N/A</span>
                    )}
                  </div>
                  <div>
                    <strong>{t('box_number')}: </strong>
                    {record.boxNumber?.length > 0 && (
                      <span>{record.boxNumber}</span>
                    )}
                    {!record.boxNumber?.length > 0 && (
                      <span className="text-gray-400">N/A</span>
                    )}
                  </div>
                  <div>
                    <strong>{t('customer_po')}: </strong>
                    {record.customerPO?.length > 0 && (
                      <span>{record.customerPO}</span>
                    )}
                    {!record.customerPO?.length > 0 && (
                      <span className="text-gray-400">N/A</span>
                    )}
                  </div>
                </div>
                <div className="col-span-1">
                  <div>
                    <strong>{t('source_name')}: </strong>
                    {record.sourceName?.length > 0 && (
                      <span>{record.sourceName}</span>
                    )}
                    {!record.sourceName?.length > 0 && (
                      <span className="text-gray-400">N/A</span>
                    )}
                  </div>
                  <div>
                    <strong>{t('source_part_number')}: </strong>
                    {record.sourcePartNumber?.length > 0 && (
                      <span>{record.sourcePartNumber}</span>
                    )}
                    {!record.sourcePartNumber?.length > 0 && (
                      <span className="text-gray-400">N/A</span>
                    )}
                  </div>
                  <div>
                    <strong>{t('class')}: </strong>
                    {record.class?.length > 0 && (
                      <span>{record.class}</span>
                    )}
                    {!record.class?.length > 0 && (
                      <span className="text-gray-400">N/A</span>
                    )}
                  </div>
                  <div>
                    <strong>{t('category')}: </strong>
                    {record.category?.length > 0 && (
                      <span>{record.category}</span>
                    )}
                    {!record.category?.length > 0 && (
                      <span className="text-gray-400">N/A</span>
                    )}
                  </div>
                  <div>
                    <strong>{t('mpn')}: </strong>
                    {record.mpn?.length > 0 && (
                      <span>{record.mpn}</span>
                    )}
                    {!record.mpn?.length > 0 && (
                      <span className="text-gray-400">N/A</span>
                    )}
                  </div>
                  <div>
                    <strong>{t('ownership')}: </strong>
                    {record.ownerShip?.length > 0 && (
                      <span>{record.ownerShip}</span>
                    )}
                    {!record.ownerShip?.length > 0 && (
                      <span className="text-gray-400">N/A</span>
                    )}
                  </div>
                </div>
                <div className="col-span-1">
                  <div>
                    <strong>{t('facility')}: </strong>
                    {record.facility?.length > 0 && (
                      <span>{record.facility}</span>
                    )}
                    {!record.facility?.length > 0 && (
                      <span className="text-gray-400">N/A</span>
                    )}
                  </div>
                  <div>
                    <strong>{t('location_facility')}: </strong>
                    {record.locationFacility?.length > 0 && (
                      <span>{record.locationFacility}</span>
                    )}
                    {!record.locationFacility?.length > 0 && (
                      <span className="text-gray-400">N/A</span>
                    )}
                  </div>
                  <div>
                    <strong>{t('process_area')}: </strong>
                    {record.processArea?.length > 0 && (
                      <span>{record.processArea}</span>
                    )}
                    {!record.processArea?.length > 0 && (
                      <span className="text-gray-400">N/A</span>
                    )}
                  </div>
                  <div>
                    <strong>{t('created_by')}: </strong>
                    {record.createdBy?.length > 0 && (
                      <span>{record.createdBy}</span>
                    )}
                    {!record.createdBy?.length > 0 && (
                      <span className="text-gray-400">N/A</span>
                    )}
                  </div>
                  <div>
                    <strong>{t('notes')}: </strong>
                    {record.notes?.length > 0 && (
                      <span>{record.notes}</span>
                    )}
                    {!record.notes?.length > 0 && (
                      <span className="text-gray-400">N/A</span>
                    )}
                  </div>
                </div>
              </div>
            )
          }}
        />
      </div>
    </MainPage>
  );
}

export default PartsInventory;
